import React, { useState, useRef } from "react";
import { useSubscriptionFeatures } from "@/hooks/useSubscriptionFeatures";
import { useOrganizationBranding } from "@/hooks/useOrganizationBranding";
import { toast } from "@/hooks/use-toast";

export const BrandingManagement = () => {
  const { organizationData } = useSubscriptionFeatures();
  const { branding, loading, saving, updateBranding, uploadLogo, refreshBranding } = useOrganizationBranding();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [localBranding, setLocalBranding] = useState({
    enabled: false,
    primaryColor: '#40E0D0',
  });

  // Synchronize local state with branding from hook
  React.useEffect(() => {
    if (branding) {
      setLocalBranding({
        enabled: branding.enabled || false,
        primaryColor: branding.primaryColor || '#40E0D0',
      });
    }
  }, [branding]);

  const handleColorChange = (value: string) => {
    setLocalBranding(prev => ({
      ...prev,
      primaryColor: value
    }));
  };

  const handleSave = async () => {
    const result = await updateBranding(localBranding);
    if (!result.error) {
      await refreshBranding();
      toast({
        title: "Paramètres sauvegardés",
        description: "Vos paramètres de branding ont été mis à jour avec succès",
      });
    }
  };

  const handleLogoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validation du fichier
    if (file.size > 5 * 1024 * 1024) { // 5MB max
      toast({
        title: "Fichier trop volumineux",
        description: "Le logo ne peut pas dépasser 5MB",
        variant: "destructive",
      });
      return;
    }

    if (!file.type.startsWith('image/')) {
      toast({
        title: "Format non supporté",
        description: "Veuillez sélectionner un fichier image",
        variant: "destructive",
      });
      return;
    }

    const result = await uploadLogo(file);
    if (!result.error) {
      await refreshBranding();
      toast({
        title: "Logo téléchargé",
        description: "Votre logo a été téléchargé avec succès",
      });
    }

    // Clear the file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  if (loading) {
    return (
      <div className="self-stretch px-6 flex flex-col justify-start items-start gap-5">
        <div className="w-full text-center py-8 text-neutral-400">
          Chargement des paramètres...
        </div>
      </div>
    );
  }

  if (!organizationData) {
    return (
      <div className="self-stretch px-6 flex flex-col justify-start items-start gap-5">
        <div className="w-full text-center py-8 text-neutral-400">
          Aucune organisation trouvée
        </div>
      </div>
    );
  }

  return (
    <div className="self-stretch px-6 flex flex-col justify-start items-start gap-5">
      {/* En-tête */}
      <div className="w-[1066px] flex flex-col justify-start items-start gap-1">
        <div className="self-stretch justify-start text-gray-800 text-2xl font-medium font-['Bricolage_Grotesque']">Paramètres de branding</div>
        <div className="justify-center text-neutral-400 text-sm font-medium font-['Bricolage_Grotesque']">Personnalisez l'apparence de vos campagnes et rapports avec votre identité visuelle</div>
      </div>

      {/* Branding personnalisé */}
      <div className="self-stretch p-5 rounded-xl outline outline-1 outline-offset-[-1px] outline-gray-200 inline-flex justify-between items-center">
        <div className="size- inline-flex flex-col justify-center items-start gap-1">
          <div className="justify-start text-black text-xl font-medium font-['Bricolage_Grotesque']">Branding personnalisé</div>
          <div className="justify-start text-neutral-400 text-sm font-medium font-['Bricolage_Grotesque']">Activez le branding personnalisé pour utiliser votre logo et vos couleurs</div>
        </div>
        <div
          className={`w-12 h-7 p-1 rounded-[55.36px] flex items-center overflow-hidden cursor-pointer transition-colors ${localBranding.enabled ? 'bg-teal-400 justify-end' : 'bg-neutral-400/20 justify-start'
            }`}
          onClick={() => setLocalBranding(prev => ({ ...prev, enabled: !prev.enabled }))}
        >
          <div data-svg-wrapper className="relative">
            <svg width="22" height="23" viewBox="0 0 22 23" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="0.571289" y="0.785645" width="21.4286" height="21.4286" rx="10.7143" fill="white" />
            </svg>
          </div>
        </div>
      </div>

      {/* Logo de l'organisation */}
      <div className="self-stretch p-5 rounded-xl outline outline-1 outline-offset-[-1px] outline-gray-200 inline-flex justify-between items-center">
        <div className="size- inline-flex flex-col justify-center items-start gap-1">
          <div className="justify-start text-black text-xl font-medium font-['Bricolage_Grotesque']">Logo de l'organisation</div>
          <div className="justify-start text-neutral-400 text-sm font-medium font-['Bricolage_Grotesque']">Téléchargez le logo de votre organisation (Format recommandé: PNG ou SVG, max 5MB)</div>
        </div>
        <div className="h-7 px-2 py-1.5 rounded-lg outline outline-1 outline-offset-[-1px] outline-neutral-400/20 flex justify-center items-center gap-1 cursor-pointer" onClick={() => fileInputRef.current?.click()}>
          <div data-svg-wrapper className="relative">
            <svg width="14" height="15" viewBox="0 0 14 15" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M5.43652 7.31348L6.92986 8.80681L8.42319 7.31348" stroke="#8F949D" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M6.92969 2.8335V8.766" stroke="#8F949D" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M11.6663 7.60498C11.6663 10.1833 9.91634 12.2716 6.99967 12.2716C4.08301 12.2716 2.33301 10.1833 2.33301 7.60498" stroke="#8F949D" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <div className="justify-start"><span className="text-neutral-400 text-xs font-medium font-['Bricolage_Grotesque'] uppercase tracking-tight">T</span><span className="text-neutral-400 text-xs font-medium font-['Bricolage_Grotesque'] lowercase tracking-tight">élécharger un logo</span></div>
        </div>
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleLogoUpload}
          accept="image/*"
          className="hidden"
        />
      </div>

      {/* Palette de couleurs */}
      <div className="self-stretch p-5 rounded-xl outline outline-1 outline-offset-[-1px] outline-gray-200 flex flex-col justify-center items-start gap-5">
        <div className="size- flex flex-col justify-center items-start gap-1">
          <div className="justify-start text-black text-xl font-medium font-['Bricolage_Grotesque']">Palette de couleurs</div>
          <div className="justify-start text-neutral-400 text-sm font-medium font-['Bricolage_Grotesque']">Définissez les couleurs principales de votre identité visuelle</div>
        </div>
        <div className="w-96 inline-flex justify-start items-center gap-5">
          <div className="flex-1 inline-flex flex-col justify-start items-start gap-2">
            <div className="self-stretch justify-center text-neutral-400 text-sm font-medium font-['Bricolage_Grotesque']">Couleur principale</div>
            <div className="self-stretch h-11 px-3 bg-white rounded-[10px] outline outline-1 outline-offset-[-1px] outline-neutral-400/20 inline-flex justify-start items-center gap-2.5">
              <div data-svg-wrapper>
                <svg width="25" height="26" viewBox="0 0 25 26" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect y="0.5" width="25" height="25" rx="6" fill={localBranding.primaryColor} />
                </svg>
              </div>
              <input
                type="text"
                value={localBranding.primaryColor}
                onChange={(e) => handleColorChange(e.target.value)}
                className="flex-1 bg-transparent text-gray-800 text-sm font-medium font-['Bricolage_Grotesque'] outline-none"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Aperçu */}
      <div className="self-stretch p-5 rounded-xl outline outline-1 outline-offset-[-1px] outline-gray-200 flex flex-col justify-center items-start gap-5">
        <div className="justify-start text-neutral-400 text-xl font-medium font-['Bricolage_Grotesque']">Aperçu</div>
        <div className="w-72 inline-flex justify-between items-center">
          {branding?.logoUrl ? (
            <div className="flex items-center">
              <img
                src={branding.logoUrl}
                alt="Logo organisation"
                className="h-8 w-auto max-w-[200px] object-contain"
              />
            </div>
          ) : (
            <div className="justify-center text-gray-800 text-xl font-medium font-['Bricolage_Grotesque']">Leaders OS</div>
          )}
          <div className="size- flex justify-start items-center gap-3">
            <div className="h-8 px-3 py-2 rounded-lg outline outline-1 outline-offset-[-1px] outline-neutral-400/20 flex justify-start items-center gap-1" style={{ backgroundColor: localBranding.primaryColor }}>
              <div className="justify-center text-white text-sm font-medium font-['Bricolage_Grotesque']">Couleur principale</div>
            </div>
          </div>
        </div>
      </div>

      {/* Bouton de sauvegarde */}
      <div className="h-10 p-3 bg-teal-400 rounded-[10px] inline-flex justify-start items-center gap-1 overflow-hidden cursor-pointer" onClick={handleSave} style={{ opacity: saving ? 0.5 : 1, pointerEvents: saving ? 'none' : 'auto' }}>
        <div className="justify-center text-white text-sm font-semibold font-['Bricolage_Grotesque']">
          {saving ? "Sauvegarde..." : "Sauvegarder les paramètres"}
        </div>
      </div>
    </div>
  );
};